/**
 * A package as the Packages list shows it — not `OrderablePackage` (orders.ts), which is
 * deliberately the narrower slice the create-order flow needs. This is the fuller record: where it
 * is, whether the warehouse has measured it yet, and what it costs to render a row without an
 * order in hand.
 *
 * MYP-40 wires `package_list` onto this; nothing here is a wire shape.
 */

import { normaliseIdentifier } from './search.js';

/**
 * Where a package is in its life, as this app groups it — six buckets over the vendor's own
 * twenty-two status codes (`package_status_list`). The grouping drives the tabs, the lock rule and
 * the chip colour; the exact vendor state is carried alongside on `statusCode`/`statusLabel` and
 * is what the customer actually reads, so nothing is flattened away in the telling.
 *
 * `arrived` and `on_the_way` both precede intake — nothing has been weighed or shelved yet, so
 * `weightGrams`/`arrivedAt` are unset for both. They stay two states rather than one because a
 * customer asking "where is it" wants to know whether it has reached the warehouse gate at all.
 *
 * `exception` is the four states that are not a position on that track at all but an outcome that
 * stopped it: lost, refused by the warehouse, destroyed, returned. They get their own bucket
 * rather than being folded into the nearest neighbour because a lost parcel rendered as an
 * ordinary in-warehouse row is the app quietly telling the customer something untrue.
 */
export type PackageStatus =
  'on_the_way' | 'arrived' | 'in_warehouse' | 'in_order' | 'shipped' | 'exception';

/**
 * A package's measured footprint in centimetres, set by the warehouse at intake — same reasoning
 * as `weightGrams`: unset until then, since nothing has actually measured it yet.
 */
export interface PackageDimensionsCm {
  readonly length: number;
  readonly width: number;
  readonly height: number;
}

export interface Package {
  readonly id: string;
  readonly trackingNumber: string;
  readonly itemNameEn: string;
  /**
   * The item's own name in Chinese, for a Chinese-speaking warehouse to read directly — not a
   * translation of `itemNameEn` and never run through `t()`, the same way `itemNameEn` isn't.
   * Optional: a customer may not have (or need) a Chinese name for everything they forward.
   */
  readonly itemNameCn?: string;
  readonly quantity: number;
  readonly warehouseId: string;
  readonly status: PackageStatus;
  /**
   * The vendor's own code for this package's state, e.g. `'DELAYIN'` or `'WAREHOUSEREJECTION'` —
   * one of the twenty-two `package_status_list` publishes, not one of the six `status` groups it
   * was mapped into. Kept so a row can say precisely where the package is even though the tabs
   * and the lock rule reason in coarser terms.
   */
  readonly statusCode: string;
  /**
   * The vendor's own label for `statusCode`, already resolved to the reader's language from the
   * `status_en`/`status_cn` pair `package_status_list` supplies. Not run through `t()`: these are
   * the vendor's words for its own workflow, the same reasoning `itemNameEn` and a warehouse's
   * `label` follow. Falls back to the raw code when the dictionary has no entry, so a status the
   * vendor adds tomorrow renders as `REPACKED` rather than as nothing.
   */
  readonly statusLabel: string;
  /**
   * ISO timestamp of warehouse intake — the moment it was actually weighed and shelved, not when
   * it was created or first scanned. Unset until then, which is what "awaiting intake" on a row
   * means and what makes `daysInWarehouse` well-defined.
   */
  readonly arrivedAt?: string;
  readonly weightGrams?: number;
  /** Set at the same intake moment `weightGrams` is — see that field's doc comment. */
  readonly dimensionsCm?: PackageDimensionsCm;
  /** The warehouse's own shelf/bin code, e.g. `'JP1.0'` — set at intake, shown for context only. */
  readonly shelfLocation?: string;
  /** The courier itself, e.g. `'SF Express'` — kept apart from `trackingNumber` since a customer
   * enters them as two separate facts (MYP-37). Optional: not every package names its courier. */
  readonly courierName?: string;
  /**
   * Customs/insurance value in cents — fen, since it is in RMB (`DECLARED_VALUE_CURRENCY`,
   * MYP-145). Optional, same reasoning `remark` has.
   */
  readonly declaredValueCents?: number;
  /**
   * The customer's own note about this package (MYP-37). The old website says it is only for the
   * customer's own use (只用于顾客自行备注) — the warehouse does not read it (MYP-145).
   */
  readonly remark?: string;
  /** Warehouse-service codes already applied, e.g. `'VACUUM_COMPRESSION'`. Empty when none are. */
  readonly serviceCodes: readonly string[];
  /**
   * Set once the package has been ticked onto an order, whether or not that order has shipped yet
   * — a package can be `in_order` and still `awaiting intake` if the warehouse hasn't weighed it.
   */
  readonly orderNumber?: string;
  /**
   * `goodstype` — a code from the vendor's `goods_type` dictionary (MYP-102), not a free string.
   * Optional: live rows do arrive without one (old, mostly closed packages), and inventing GENERAL
   * for them would count them under a filter they were never declared for.
   */
  readonly goodsType?: string;
  /**
   * `pkgnum` — how many parcels this tracking number covers (MYP-123). Not shown; kept only so an
   * edit can send it back. `add_packages` updates by resubmitting the whole package, and a field
   * left out falls back to its default (1) — live, 15 of MP66184's 40 free packages have more than
   * one parcel, which an edit would otherwise quietly reset.
   */
  readonly parcelCount?: number;
  /**
   * `transtype` (AIR / SEA / UNC) — not shown, kept for the same reason as `parcelCount`: left out
   * of an update, the vendor writes UNC over whatever was there.
   */
  readonly transportCode?: string;
}

/**
 * The currency a package's declared value is in: **RMB** (MYP-145). The old website asks for
 * 申报价值 in RMB and `declareprice` carries no currency, so the server reads the number as RMB.
 * Labelling or showing it as RM would have a customer declare about a third less than they mean —
 * for customs, where a wrong declaration can get the parcel held or fined.
 */
export const DECLARED_VALUE_CURRENCY = 'RMB';

/** Whole days since intake, or undefined for a package the warehouse hasn't received yet. */
export function daysInWarehouse(pkg: Package, now: Date): number | undefined {
  if (!pkg.arrivedAt) return undefined;
  return Math.floor((now.getTime() - Date.parse(pkg.arrivedAt)) / 86_400_000);
}

/**
 * Once a package is tied to an order it stops being editable here — same reasoning as
 * `isOrderEditable`: what's on the order was already fixed when it was ticked on, so this list's
 * job past that point is to show the package, not to offer to change it.
 */
export function isPackageLocked(pkg: Pick<Package, 'status' | 'orderNumber'>): boolean {
  // `exception` locks for a different reason than the other two: not "already committed to an
  // order" but "no longer a package anyone can act on" — a destroyed or returned parcel is not
  // something to edit, delete or tick onto an order.
  if (pkg.status === 'in_order' || pkg.status === 'shipped' || pkg.status === 'exception') {
    return true;
  }
  /*
    A package keeps NEW or IN while the order it is on is still being put together (MYP-90): live
    on 2026-09-25, 132 of the 162 NEW/ARRIVE/IN packages carried an `outorderno`, on orders at
    ORDERED, OUT, PENDING and PARTWFD. The status group alone called them free — selectable for a
    second order, editable, deletable. The order number is what says they are taken.
  */
  return pkg.orderNumber !== undefined && pkg.orderNumber.trim() !== '';
}

/**
 * The list's own tabs. `on_the_way` and `in_order` are each two statuses collapsed into one tab —
 * the four-tab shape the design settled on cares about "is it here yet" and "has it left for an
 * order", not the finer states in between.
 *
 * MYP-32 folds this into `PackageFilters` below rather than keeping it as separate screen state:
 * once there's a real filter bar with its own Reset, a tab that Reset can't touch and a filter set
 * it can are two systems fighting over one list.
 */
export type PackageTab = 'all' | 'on_the_way' | 'in_warehouse' | 'in_order';

export const PACKAGE_TABS: readonly PackageTab[] = [
  'all',
  'on_the_way',
  'in_warehouse',
  'in_order',
];

const TAB_STATUSES: Record<Exclude<PackageTab, 'all'>, readonly PackageStatus[]> = {
  on_the_way: ['arrived', 'on_the_way'],
  in_warehouse: ['in_warehouse'],
  in_order: ['in_order', 'shipped'],
};

export function packagesInTab(packages: readonly Package[], tab: PackageTab): readonly Package[] {
  if (tab === 'all') return packages;
  const statuses = TAB_STATUSES[tab];
  return packages.filter((pkg) => statuses.includes(pkg.status));
}

export function countPackagesByTab(packages: readonly Package[]): Record<PackageTab, number> {
  const counts = {} as Record<PackageTab, number>;
  for (const tab of PACKAGE_TABS) counts[tab] = packagesInTab(packages, tab).length;
  return counts;
}

/* -------------------------------------------------------------------------------------------- *
 * Filtering (MYP-32)
 * -------------------------------------------------------------------------------------------- */

export type PackageSort = 'newest' | 'oldest';

export interface PackageFilters {
  readonly tab: PackageTab;
  /**
   * A package number, as typed. Matched with separators stripped — see `matchesPackageSearch`.
   * Empty means "everything", which is why it is a string rather than `string | undefined`: there
   * is no meaningful difference between an empty box and an absent one.
   */
  readonly search: string;
  /** A warehouse id, or `'all'`. */
  readonly warehouseId: string;
  /** A code from the vendor's `goods_type` dictionary, or `'all'`. */
  readonly goodsType: string;
  readonly sort: PackageSort;
  /**
   * Storage risk is deliberately not filtered by `filterPackages` below — whether a package is
   * overdue depends on its warehouse's free-storage window, which is vendor data the warehouses
   * feature fetches and this package cannot reach. The web layer applies this one as an extra pass, using
   * the same calculation `PackageListRow`'s warning line already does, so the checkbox and the row
   * can never disagree about which packages are actually at risk.
   */
  readonly storageRiskOnly: boolean;
}

export const EMPTY_PACKAGE_FILTERS: PackageFilters = {
  tab: 'all',
  search: '',
  warehouseId: 'all',
  goodsType: 'all',
  sort: 'newest',
  storageRiskOnly: false,
};

/** Whether anything is narrowing the list — drives whether a Reset control is worth showing. */
export function hasActivePackageFilters(filters: PackageFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.tab !== EMPTY_PACKAGE_FILTERS.tab ||
    filters.warehouseId !== EMPTY_PACKAGE_FILTERS.warehouseId ||
    filters.goodsType !== EMPTY_PACKAGE_FILTERS.goodsType ||
    filters.sort !== EMPTY_PACKAGE_FILTERS.sort ||
    filters.storageRiskOnly
  );
}

/**
 * Whether a package answers to the search box.
 *
 * Package numbers only — that is what the box asks for, and what the label promises. Separators
 * are stripped on both sides (`normaliseIdentifier`), since a number read off a courier's SMS is
 * retyped without its spaces and dashes as often as with them. A blank query matches everything,
 * so the box is inert until someone types in it.
 */
export function matchesPackageSearch(pkg: Package, query: string): boolean {
  const trimmed = query.trim();
  if (trimmed === '') return true;

  return normaliseIdentifier(pkg.trackingNumber).includes(normaliseIdentifier(trimmed));
}

function matchesWarehouse(pkg: Package, warehouseId: string): boolean {
  return warehouseId === 'all' || pkg.warehouseId === warehouseId;
}

function matchesGoodsType(pkg: Package, goodsType: string): boolean {
  return goodsType === 'all' || pkg.goodsType === goodsType;
}

/**
 * Tab, warehouse and goods-type applied, then sorted. Storage risk is a separate pass — see
 * `storageRiskOnly` above.
 *
 * `now` stands in for a package's intake time when it doesn't have one yet: an awaiting-intake
 * package is still the most recently active thing in the list, just not yet weighed, so it sorts
 * to the top under "newest first" rather than falling to the bottom for lack of a timestamp.
 */
export function filterPackages(
  packages: readonly Package[],
  filters: PackageFilters,
  now: Date,
): readonly Package[] {
  const matched = packagesInTab(packages, filters.tab).filter(
    (pkg) =>
      matchesPackageSearch(pkg, filters.search) &&
      matchesWarehouse(pkg, filters.warehouseId) &&
      matchesGoodsType(pkg, filters.goodsType),
  );

  return [...matched].sort((a, b) => {
    const aTime = a.arrivedAt ? Date.parse(a.arrivedAt) : now.getTime();
    const bTime = b.arrivedAt ? Date.parse(b.arrivedAt) : now.getTime();
    const oldestFirst = aTime - bTime;
    return filters.sort === 'oldest' ? oldestFirst : -oldestFirst;
  });
}

/**
 * How many packages sit in each tab, given the *other* filters (warehouse, goods-type).
 * Deliberately ignores `filters.tab` and `filters.storageRiskOnly` for the same reason
 * `countOrdersByStatus` ignores `filters.status` — counted after its own dimension, every tab but
 * the active one would read zero. Storage risk stays out because it isn't computable here (see
 * `storageRiskOnly` above); the tab counts run slightly optimistic while that checkbox is on, and
 * the "shown" total the web layer computes after both passes is the number that's actually true.
 */
export function countPackagesByFilters(
  packages: readonly Package[],
  filters: PackageFilters,
): Record<PackageTab, number> {
  const pool = packages.filter(
    (pkg) =>
      matchesPackageSearch(pkg, filters.search) &&
      matchesWarehouse(pkg, filters.warehouseId) &&
      matchesGoodsType(pkg, filters.goodsType),
  );
  return countPackagesByTab(pool);
}

/**
 * Reads filters back out of a URL. Everything is validated against what actually exists, so a
 * hand-edited or stale link degrades to the default view instead of rendering an empty list under
 * a warehouse or goods-type nothing can ever match.
 */
export function parsePackageFilters(
  params: URLSearchParams,
  knownWarehouseIds: readonly string[],
  knownGoodsTypes: readonly string[],
): PackageFilters {
  const tab = params.get('tab') ?? '';
  const search = params.get('q') ?? '';
  const warehouseId = params.get('warehouse') ?? '';
  const goodsType = params.get('goodsType') ?? '';
  const sort = params.get('sort') ?? '';

  return {
    tab: PACKAGE_TABS.includes(tab as PackageTab) ? (tab as PackageTab) : 'all',
    // Not validated against anything: any string is a legitimate query, including one that
    // matches nothing. The no-matches empty state is the answer to that, not a silent reset.
    search,
    warehouseId: knownWarehouseIds.includes(warehouseId) ? warehouseId : 'all',
    goodsType: knownGoodsTypes.includes(goodsType) ? goodsType : 'all',
    sort: sort === 'oldest' ? 'oldest' : 'newest',
    storageRiskOnly: params.get('risk') === '1',
  };
}

/* -------------------------------------------------------------------------------------------- *
 * Add/Edit (MYP-33, MYP-34)
 * -------------------------------------------------------------------------------------------- */

/**
 * The fields a customer can actually change about a package, or fill in to add a new one.
 * Deliberately narrower than `Package` itself: `status`, `arrivedAt`, `weightGrams` and
 * `orderNumber` are all set by the warehouse or by ticking a package onto an order, not typed in
 * by hand here.
 *
 * `warehouseId` and `serviceIds` (MYP-34) are the exception, and only half-editable at that: a
 * package's warehouse is a physical fact once it exists there, so the web layer only lets
 * `warehouseId` be *set* in Add mode — Edit mode seeds it from the existing package and shows it
 * read-only. `serviceIds` follows whichever warehouse is current either way, since which services
 * are even offered depends on it (`servicesFor` in the packages provider, from `packages_services`).
 */
export interface PackageDraft {
  /** The courier's own tracking number — pasted in by the customer, not warehouse-assigned. */
  readonly trackingNumber: string;
  /**
   * The courier itself, e.g. `'SF Express'`. Not asked for on the form since MYP-145 — the old
   * website does not ask either — but kept, so an edit sends a package's existing one back as it was.
   */
  readonly courierName: string;
  readonly itemNameEn: string;
  /**
   * The Chinese item name. Optional: the vendor document marks `declarecnname` as not required on
   * `add_packages` (mentor, MYP-145 review), so an empty one is simply left out of the request.
   */
  readonly itemNameCn: string;
  readonly quantity: number;
  /** Cents, or `undefined` for "not declared" — kept distinct from `0`, a real declared value. */
  readonly declaredValueCents: number | undefined;
  readonly remark: string;
  /**
   * A code from the vendor's `goods_type` dictionary, or `''` for none chosen — the case for an
   * existing package the vendor holds no goods type for (see `Package.goodsType`).
   */
  readonly goodsType: string;
  readonly warehouseId: string;
  /** `WarehouseService` ids, scoped to whichever warehouse `warehouseId` currently names. */
  readonly serviceIds: readonly string[];
}

export const EMPTY_PACKAGE_DRAFT: PackageDraft = {
  trackingNumber: '',
  courierName: '',
  itemNameEn: '',
  itemNameCn: '',
  quantity: 1,
  declaredValueCents: undefined,
  remark: '',
  // The vendor's own default: `add_packages` documents goodstype as 默认 GENERAL.
  goodsType: 'GENERAL',
  warehouseId: '',
  serviceIds: [],
};

/** A draft seeded from a package that already exists, for the Edit half of this form. */
export function packageDraftFrom(pkg: Package): PackageDraft {
  return {
    trackingNumber: pkg.trackingNumber,
    courierName: pkg.courierName ?? '',
    itemNameEn: pkg.itemNameEn,
    itemNameCn: pkg.itemNameCn ?? '',
    quantity: pkg.quantity,
    declaredValueCents: pkg.declaredValueCents,
    remark: pkg.remark ?? '',
    goodsType: pkg.goodsType ?? '',
    warehouseId: pkg.warehouseId,
    serviceIds: pkg.serviceCodes,
  };
}

/**
 * Whether a draft is complete enough to submit (MYP-129): a tracking number, a warehouse, an item
 * name, a quantity of at least 1 and a declared value above 0 — the fields the Add package form
 * marks with *. The vendor document marks only the tracking number as required and calls the rest
 * "should fill"; the declared value is required here because customs needs it. Courier company,
 * Chinese item name, remarks and services stay optional.
 */
export function isPackageDraftComplete(draft: PackageDraft): boolean {
  return (
    draft.itemNameEn.trim().length > 0 &&
    draft.trackingNumber.trim().length > 0 &&
    draft.quantity > 0 &&
    draft.warehouseId.length > 0 &&
    draft.declaredValueCents !== undefined &&
    draft.declaredValueCents > 0
  );
}

/* -------------------------------------------------------------------------------------------- *
 * Messages (MYP-38)
 * -------------------------------------------------------------------------------------------- */

/**
 * Which way a package message travelled — W2U (warehouse→user) or U2W (user→warehouse), the same
 * shorthand the ticket itself uses. Drives which side a message's bubble sits on, not just a label:
 * the warehouse's words and the customer's own read differently and should look different.
 */
export type PackageMessageDirection = 'W2U' | 'U2W';

/**
 * One message in a package's thread with its warehouse — `get_packages_msg`/`post_packages_msg`.
 * The vendor API doc these come from isn't in this repo (`scripts/generate-legacy-endpoints.mjs`'s
 * own comment explains why), so this shape is inferred from the endpoint titles and the ticket's
 * own description rather than a documented field list. Whoever wires these endpoints up for real
 * is what confirms it.
 */
export interface PackageMessage {
  readonly id: string;
  readonly packageId: string;
  readonly direction: PackageMessageDirection;
  readonly content: string;
  /** ISO timestamp. */
  readonly createdAt: string;
}

/** A package's own thread, oldest message first — a conversation reads top to bottom. */
export function packageMessagesFor(
  messages: readonly PackageMessage[],
  packageId: string,
): readonly PackageMessage[] {
  return messages
    .filter((message) => message.packageId === packageId)
    .slice()
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

/* -------------------------------------------------------------------------------------------- *
 * Pagination
 * -------------------------------------------------------------------------------------------- */

/**
 * Rows per page.
 *
 * Client-side, over the whole filtered list. `package_list` does support `page`/`limit`, but this
 * screen filters, counts its tabs and sorts across every package the account has — all of which
 * would be wrong computed over one server page. The list is fetched once with `limit: -1` and cut
 * up here, so a page is only ever a slice of what the filters already decided.
 */
export const PACKAGES_PER_PAGE = 15;

/** How many pages a list of this length needs. Always at least 1, so "page 1 of 0" cannot happen. */
export function packagePageCount(total: number, perPage: number = PACKAGES_PER_PAGE): number {
  return Math.max(1, Math.ceil(total / perPage));
}

/**
 * Clamps a page number into the range that actually exists.
 *
 * Out-of-range is routine rather than exceptional here: the page lives in the URL, so a bookmark
 * survives to a day when the account has fewer packages, and narrowing a filter can shrink a
 * 4-page list to 1 while the reader is on page 3. Clamping shows them the last page that exists
 * instead of an empty one.
 */
export function clampPackagePage(page: number, total: number, perPage: number = PACKAGES_PER_PAGE) {
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(Math.trunc(page), 1), packagePageCount(total, perPage));
}

/** The slice of `packages` shown on `page`, which is clamped first — see `clampPackagePage`. */
export function packagesOnPage(
  packages: readonly Package[],
  page: number,
  perPage: number = PACKAGES_PER_PAGE,
): readonly Package[] {
  const start = (clampPackagePage(page, packages.length, perPage) - 1) * perPage;
  return packages.slice(start, start + perPage);
}

/** Reads `?page=` back out of a URL. Anything unparseable is page 1, same spirit as the filters. */
export function parsePackagePage(params: URLSearchParams): number {
  const parsed = Number(params.get('page'));
  return Number.isFinite(parsed) && parsed >= 1 ? Math.trunc(parsed) : 1;
}
